import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _every from 'lodash/every';
import SelectPanel from '@lib/components/SelectPanel/SelectPanel';
import { SectorOption, SiteOption } from '@lib/components/SelectPanel/options';
import { GetLoadOptions } from '@lib/components/Select/types';
import { getOptionsArrayValidation } from '@lib/utils/validationUtils';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import generateIdsFieldKey from '@lib/utils/generateIdsFieldKey';
import formStyles from '@lib/assets/styles/Form.module.scss';
import { materialSymbolsOutlined } from '@lib/enums/common';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import { FieldNames } from '../enums';

export default function useFilterFields({
  getSitesLoadOptions,
  getSectorsLoadOptions,
  getAssetCategoriesLoadOptions,
  clearFilter,
}: {
  getSitesLoadOptions: GetLoadOptions;
  getSectorsLoadOptions: GetLoadOptions;
  getAssetCategoriesLoadOptions: GetLoadOptions;
  clearFilter: () => void;
}): FieldItem[] {
  const { t } = useTranslation();
  return [
    {
      name: FieldNames.Sectors,
      label: t('sector'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      componentProps: {
        defaultOptions: false,
        getLoadOptions: getSectorsLoadOptions,
        renderOption: SectorOption,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        buttonProps: {
          leftIcon: 'share_location',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      fieldChangeCallBack: ({
        type,
        setValue,
        name,
      }: FieldChangeCallBackArgs) => {
        if (type === 'change' && name === FieldNames.Sectors) {
          setValue(FieldNames.Sites, [], {
            shouldValidate: false,
          });
        }
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.Sites,
      label: t('site'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      componentProps: {
        defaultOptions: false,
        getLoadOptions: getSitesLoadOptions,
        renderOption: SiteOption,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        buttonProps: {
          leftIcon: 'home_pin',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      getFieldKeyFn: (values: Values) => {
        const rawSectorIds = _get(values, FieldNames.Sectors, []) as Array<
          { value?: string } | string
        >;
        return generateIdsFieldKey(rawSectorIds, {
          prefix: `${FieldNames.Sectors}-sites`,
          emptyKey: `${FieldNames.Sectors}-no-sites`,
        });
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.Categories,
      label: t('category'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      componentProps: {
        getLoadOptions: getAssetCategoriesLoadOptions,
        defaultOptions: false,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        buttonProps: {
          leftIcon: 'category',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.Reset,
      label: '',
      customRender: () => (
        <Button
          buttonSize={ButtonSizes.large}
          buttonText={t('reset')}
          buttonType={ButtonTypes.primaryText}
          onClick={clearFilter}
        />
      ),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isOptionalFn: () => true,
      isVisibleFn: (values: Values) => {
        const sectors = _get(values, FieldNames.Sectors, []);
        const sites = _get(values, FieldNames.Sites, []);
        const categories = _get(values, FieldNames.Categories, []);
        return !_every([sectors, sites, categories], _isEmpty);
      },
    },
  ];
}

export { FieldNames } from '../enums';
